import { useAppSelector } from 'hooks/redux';
import React, { useState, useCallback, useEffect, type JSX } from 'react';

import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import CategoryName from './CategoryName';
import CategoryParent from './CategoryParent';
import TaxInfoType from './TaxInfoType';
import TaxInfoUsageType from './TaxInfoUsageType';

import StdDialog from 'components/Dialogs/StdDialog';
import StdDialogActions from 'components/Dialogs/StdDialogActions';
import StdDialogContent from 'components/Dialogs/StdDialogContent';

import { useField, useManager, useSelector } from 'app-components/flux/categories/categoryFormHooks';

import InfoIcon from '@mui/icons-material/Info';
import { Card } from '@mui/material';
import type { CATEGORIES_REDUCER_KEY } from 'app-components/flux/categories';
import { getCategoryById } from 'app-components/flux/categories/categoriesSelectors';
import { disabledIfNotEditable } from 'app-components/flux/categories/categoryFormBuilder';
import type { RootState } from 'store';
import { makeStyles } from 'tss-react/mui';
import TaxInfoQBPBusiness from './TaxInfoQBPBusiness';
import TaxInfoQBPPersonal from './TaxInfoQBPPersonal';
import TaxInfoQBPSelectTaxGroups from './TaxInfoQBPSelectTaxGroups';
import TaxInfoSimplifi from './TaxInfoSimplifi';
import TaxNavButton from './TaxNavButton';
import { entitlementsSelectors } from 'app-components/flux/entitlements';

type State = Pick<RootState, typeof CATEGORIES_REDUCER_KEY>;

type Props = {
  onClose: () => void;
  enableUsageTypes: boolean;
  enableNewCategorySettings: boolean;
  enableTaxItems: boolean;
};

type StepProps = {
  dialog: {
    title: string;
    showBackButton: boolean;
    showCloseButton: boolean;
    onBack?: () => void;
  };
  actions: {
    primary: {
      id: 'primary-button';
      disabled: boolean;
      children: string;
      type: 'submit';
      onClick: () => void;
    };
    secondary: {
      id: string;
      children: string;
      type?: 'submit' | 'button';
      onClick: () => void;
      variant?: 'outlined';
    };
  };
};

const useStyles = makeStyles()((theme) => ({
  infoIcon: {
    color: theme.palette.info.main,
  },
  nonEditCard: {
    backgroundColor: theme.palette.info.light,
    padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
    display: 'flex',
    flexDirection: 'row',
    columnGap: theme.spacing(1),
    alignItems: 'center',
    marginBottom: theme.spacing(3),
  },
}));

const NonEditableAlert = () => {
  const isNotEditable = useSelector((s) => disabledIfNotEditable(s.state));
  const { classes } = useStyles();
  return isNotEditable ? (
    <Card elevation={0} className={classes.nonEditCard}>
      <InfoIcon className={classes.infoIcon} />
      <Typography variant='paragraphRegular'>{'You can only edit the Tax Info section for this category'}</Typography>
    </Card>
  ) : null;
};

const UpsertCategoryForm = ({
  onClose,
  enableUsageTypes,
  // enableNewCategorySettings,
  enableTaxItems,
}: Props): JSX.Element => {
  const manager = useManager();
  const { value: usageType } = useField('usageType');
  const [step, setStep] = useState(0);
  const [stepVariables, setStepVariables] = useState<StepProps>();
  const [isSubmitting, setSubmitting] = useState(false);

  const isEdit = !!manager.getFieldValue('id');

  const businessEnabled = useAppSelector(entitlementsSelectors.getBusinessEnabled);

  const { value: parentId } = useField('parent');
  const parentCategory = useAppSelector((state) => getCategoryById(state as State, parentId));

  const handleSubmit = useCallback(async () => {
    // Don't allow submit from step 2 - there is no values we need to validate on this step, so go back to Step 1.
    if (step === 2) {
      setStep(1);
      return;
    }

    setSubmitting(true);
    await manager.submit(step === 0);
    setSubmitting(false);

    if (manager.getFieldError('name') || manager.getFieldError('parent')) {
      setStep(0);
    } else if (!manager.isFormValid() && step === 0) {
      setStep(1);
    } else if (manager.isFormValid() && step === 1) {
      setStep(0);
    }
  }, [manager, step]);

  const toggleTaxInfo = useCallback(() => {
    const newStep = step === 0 ? 1 : 0;
    setStep(newStep);
  }, [step]);

  useEffect(() => {
    if (step === 0) {
      setStepVariables({
        dialog: {
          title: `${isEdit ? 'Edit' : 'Create'} Category`,
          showBackButton: false,
          showCloseButton: true,
        },
        actions: {
          primary: {
            id: 'primary-button',
            disabled: isSubmitting,
            children: isEdit ? 'Update' : 'Create',
            type: 'submit',
            onClick: handleSubmit,
          },
          secondary: {
            id: 'category-tax-step0-secondary-btn',
            children: 'Cancel',
            type: 'button',
            onClick: onClose,
            variant: 'outlined',
          },
        },
      });
    }
    if (step === 1) {
      setStepVariables({
        dialog: {
          title: 'Tax info',
          showBackButton: true,
          showCloseButton: false,
          onBack: () => setStep(0),
        },
        actions: {
          primary: {
            id: 'primary-button',
            disabled: isSubmitting,
            children: 'Update',
            type: 'submit',
            onClick: handleSubmit,
          },
          secondary: {
            id: 'category-tax-step1-secondary-btn',
            children: 'Cancel',
            type: 'button',
            onClick: () => setStep(0),
            variant: 'outlined',
          },
        },
      });
    }
    if (step === 2) {
      setStepVariables({
        dialog: {
          title: 'Select tax groups',
          showBackButton: false,
          showCloseButton: false,
        },
        actions: {
          primary: {
            id: 'primary-button',
            disabled: isSubmitting,
            children: 'Update',
            type: 'submit',
            onClick: handleSubmit,
          },
          secondary: {
            id: 'category-tax-step2-secondary-btn',
            children: 'Cancel',
            type: 'button',
            onClick: () => setStep(1),
            variant: 'outlined',
          },
        },
      });
    }
  }, [handleSubmit, isEdit, isSubmitting, onClose, step]);

  return (
    <StdDialog disableBackdropClick onClose={onClose} open size='md' {...stepVariables?.dialog}>
      <StdDialogContent id='upsert_category_form_content' sx={{ p: 3 }}>
        <Stack spacing={4}>
          {step === 0 && (
            <>
              <NonEditableAlert />
              <CategoryName />
              <CategoryParent />
              <TaxInfoType parentType={parentCategory?.type} />
              {enableUsageTypes && businessEnabled && (
                <>
                  <TaxInfoUsageType parentUsageType={parentCategory?.usageType} />
                  <TaxNavButton
                    id='taxInfo'
                    text='Tax Info'
                    infoText='Associate this category with corresponding tax lines. This is reflected in the Reports > Taxes section.'
                    onClick={toggleTaxInfo}
                  />
                </>
              )}

              {enableTaxItems && !businessEnabled && <TaxInfoSimplifi />}
            </>
          )}
          <Stack>
            {step === 1 && (
              <Typography variant='bodyRegular'>
                You can associate this category with tax information. This feeds your Tax Report.
              </Typography>
            )}
            {step === 1 && usageType === 'PERSONAL' && <TaxInfoQBPPersonal />}
            {step === 1 && usageType === 'BUSINESS' && <TaxInfoQBPBusiness onSelectTaxGroup={() => setStep(2)} />}

            {step === 2 && (
              <Typography variant='bodyRegular'>Make your tax group selections for this category.</Typography>
            )}
            {step === 2 && usageType === 'BUSINESS' && <TaxInfoQBPSelectTaxGroups />}
          </Stack>
        </Stack>
      </StdDialogContent>
      {stepVariables && <StdDialogActions {...stepVariables.actions} />}
    </StdDialog>
  );
};

export default UpsertCategoryForm;
